import React, { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Typography, 
  Card, 
  Button, 
  Box, 
  Grid, 
  TextField, 
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

const CoursePlayerPage = () => {
    const { cid } = useParams();
    const videoRef = useRef(null);

    const [userNotes, setUserNotes] = useState("");
    const [saveStatus, setSaveStatus] = useState("");

    useEffect(() => {
        const savedTime = localStorage.getItem(`df_progress_${weekId}`);
        const savedNotes = localStorage.getItem(`df_notes_${weekId}`);
        
        if (savedTime && videoRef.current) {
            videoRef.current.currentTime = parseFloat(savedTime);
        }
        if (savedNotes) {
            setUserNotes(savedNotes);
        }
    }, [weekId]);

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        localStorage.setItem(`df_progress_${weekId}`, videoRef.current.currentTime);
    };

    const handleSaveNotes = () => {
        localStorage.setItem(`df_notes_${weekId}`, userNotes);
        setSaveStatus("Notes saved successfully!");
        setTimeout(() => setSaveStatus(""), 3000);
    };

    return (
        /* container-fluid combined with px-3 ensures fluid full-width scaling with safe mobile padding */
        <div className="container-fluid py-4 px-2 px-md-4" style={{ width: '100%', boxSizing: 'border-box' }}>
            
            {/* Top Navigation & Header */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                <Button 
                    component={Link} 
                    to="/learning-center" 
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    size="small"
                >
                    Back to Dashboard
                </Button>
                <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Course Module: {weekId ? weekId.replace('-', ' ').toUpperCase() : 'Course Module'}
                </Typography>
            </Box>

            {/* Main Content Grid: Full-Width Fluid Layout */}
            <Grid container spacing={3}>
                
                {/* Video Player Section: Scales fluidly across breakpoints */}
                <Grid item xs={12} lg={8} xl={9}>
                    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: '#000', width: '100%' }}>
                        {/* Responsive aspect ratio container */}
                        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
                            <video 
                                ref={videoRef}
                                controls 
                                controlsList="nodownload" 
                                disablePictureInPicture
                                autoPlay
                                onTimeUpdate={handleTimeUpdate}
                                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            >
                            <source src="https://youtu.be/w4Otri8W3QE?si=grY9OOXIdOxshdOU" />
                                {/* 
                                	<source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" type="video/mp4" />
                    			*/}            	
                    
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Curriculum Lecture & Slide Presentation
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                The presentation slides and instructor audio are fully integrated. Your playback position is automatically saved.
                            </Typography>
                        </Box>
                    </Card>
                </Grid>

                {/* Notes Section: Stacks cleanly below video on mobile, sits side-by-side on desktop */}
                <Grid item xs={12} lg={4} xl={3}>
                    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                        <div>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Student Scratchpad & Notes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Record your notes as you review the lecture. They sync locally and persist across sessions.
                            </Typography>
                            <TextField
                                multiline
                                rows={12}
                                fullWidth
                                variant="outlined"
                                placeholder="Type your notes here..."
                                value={userNotes}
                                onChange={(e) => setUserNotes(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveNotes}
                                    size="small"
                                >
                                    Save Notes
                                </Button>
                                {saveStatus && (
                                    <Typography variant="caption" color="success.main" fontWeight="bold">
                                        {saveStatus}
                                    </Typography>
                                )}
                            </Box>
                        </div>
                    </Paper>
                </Grid>

            </Grid>

        </div>
    );
};

export default CoursePlayerPage;